/**
 * Driver Pending Overlay - Module Inscription Tickets 5 & 7
 * Shows blurred overlay with document upload when driver status is PENDING_VERIFICATION or UNDER_REVIEW
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Upload, FileCheck, AlertCircle, Loader2, CheckCircle, X,
  FileText, ShieldCheck, Clock
} from 'lucide-react';
import config from 'config';

const API = `${config.API_URL}/api`;

const DOC_ICONS = {
  kbis: FileText,
  rc_pro: ShieldCheck,
  permis: FileCheck,
  carte_vtc: FileCheck,
};

export default function DriverPendingOverlay({ user, onStatusChange }) {
  const driverStatus = user?.driver_status;
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [error, setError] = useState('');
  const [allUploaded, setAllUploaded] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);

  const isPending = driverStatus === 'PENDING_VERIFICATION';
  const isUnderReview = driverStatus === 'UNDER_REVIEW';

  const fetchRequiredDocs = useCallback(async () => {
    setLoadingDocs(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API}/driver/required-documents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRequiredDocs(data.required);
        setAllUploaded(data.required.every(d => d.uploaded));
      }
    } catch {
      // silent
    } finally {
      setLoadingDocs(false);
    }
  }, []);

  useEffect(() => {
    if (isPending || isUnderReview) {
      fetchRequiredDocs();
    }
  }, [isPending, isUnderReview, fetchRequiredDocs]);

  if (!isPending && !isUnderReview) return null;

  const handleUpload = async (docType, file) => {
    setError('');
    setUploading(docType);
    try {
      const token = localStorage.getItem('access_token');
      const formData = new FormData();
      formData.append('document_type', docType);
      formData.append('file', file);

      const res = await fetch(`${API}/driver/documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Erreur lors du téléchargement');
      }

      // Refresh doc list
      await fetchRequiredDocs();

      if (data.all_required_uploaded) {
        setAllUploaded(true);
        if (onStatusChange) onStatusChange('UNDER_REVIEW');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(null);
    }
  };

  return (
    <>
      {/* Blur overlay on the whole dashboard */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="driver-pending-overlay">
        <div className="w-full max-w-lg mx-4">
          {isUnderReview || allUploaded ? (
            /* Ticket 7: Confirmation message */
            <Card className="bg-[#141414] border-[#2A2A2A]">
              <CardContent className="p-8 text-center" data-testid="under-review-message">
                <div className="w-16 h-16 bg-[#C9A24D]/10 border border-[#C9A24D]/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-[#C9A24D]" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Compte en attente de vérification</h2>
                <p className="text-[#A0A0A0] text-sm mb-4">
                  Vos documents ont été soumis avec succès. Notre équipe les examine
                  et activera votre compte dans les plus brefs délais.
                </p>
                <p className="text-[#C9A24D] text-xs">
                  Vous recevrez un email de confirmation une fois votre compte activé.
                </p>
              </CardContent>
            </Card>
          ) : (
            /* Ticket 5: Pending with upload button */
            <Card className="bg-[#141414] border-[#2A2A2A]">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-amber-500/10 border border-amber-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-amber-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2" data-testid="pending-title">Compte en attente d'activation</h2>
                <p className="text-[#A0A0A0] text-sm mb-6">
                  Pour activer votre compte chauffeur, veuillez soumettre vos documents réglementaires.
                </p>
                <Button
                  onClick={() => setShowUploadModal(true)}
                  className="bg-[#C9A24D] hover:bg-[#d4af5a] text-black font-semibold px-8"
                  data-testid="open-upload-modal-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Soumettre mes documents
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80" data-testid="upload-modal">
          <div className="w-full max-w-md mx-4">
            <Card className="bg-[#141414] border-[#2A2A2A]">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-lg font-bold text-white">Documents requis</h3>
                  <button
                    onClick={() => setShowUploadModal(false)}
                    className="text-[#666] hover:text-white"
                    data-testid="close-upload-modal"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {error && (
                  <div className="flex items-start gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/20 rounded-lg" data-testid="upload-error">
                    <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                )}

                {loadingDocs ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 text-[#C9A24D] animate-spin" />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {requiredDocs.map((doc) => {
                      const Icon = DOC_ICONS[doc.type] || FileText;
                      const isUploading = uploading === doc.type;

                      return (
                        <div
                          key={doc.type}
                          className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                            doc.uploaded
                              ? 'bg-green-500/5 border-green-500/20'
                              : 'bg-[#1A1A1A] border-[#333] hover:border-[#C9A24D]/40'
                          }`}
                          data-testid={`doc-row-${doc.type}`}
                        >
                          <div className={`p-2 rounded-lg ${doc.uploaded ? 'bg-green-500/10' : 'bg-[#C9A24D]/10'}`}>
                            {doc.uploaded ? (
                              <CheckCircle className="w-4 h-4 text-green-400" />
                            ) : (
                              <Icon className="w-4 h-4 text-[#C9A24D]" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${doc.uploaded ? 'text-green-400' : 'text-white'}`}>
                              {doc.label}
                            </p>
                            {doc.uploaded && (
                              <p className="text-xs text-green-400/60">Téléchargé</p>
                            )}
                          </div>
                          {!doc.uploaded && (
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.webp"
                                className="hidden"
                                disabled={isUploading}
                                onChange={(e) => {
                                  if (e.target.files?.[0]) {
                                    handleUpload(doc.type, e.target.files[0]);
                                  }
                                }}
                                data-testid={`file-input-${doc.type}`}
                              />
                              <div className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                isUploading
                                  ? 'bg-[#333] text-[#666]'
                                  : 'bg-[#C9A24D] text-black hover:bg-[#d4af5a] cursor-pointer'
                              }`}>
                                {isUploading ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  'Choisir'
                                )}
                              </div>
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {allUploaded && (
                  <div className="mt-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-center" data-testid="all-docs-uploaded">
                    <CheckCircle className="w-5 h-5 text-green-400 mx-auto mb-1" />
                    <p className="text-green-400 text-sm font-medium">Tous les documents ont été soumis</p>
                    <p className="text-green-400/60 text-xs mt-1">Votre dossier est en cours de vérification</p>
                  </div>
                )}

                <p className="text-[#555] text-xs mt-4 text-center">
                  Formats acceptés : PDF, JPG, PNG, WebP (max 10 Mo)
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </>
  );
}
